import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "@/components/icons";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Code } from "@/components/Code";
import { InlineEmpty, InlineError } from "@/components/ListState";
import { WerkbonStatusBadge, formatNlDate, formatEuro } from "@/components/Badges";
import { formatUren } from "@/lib/uren";

// The asset's history: the werkbonnen on that machine (round 24 — this
// replaced the Reparaties tab; migrated repairs are werkbonnen now).
export const WerkbonnenTab = ({ asset }) => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get(`/werkbonnen?asset_id=${asset.id}`); setRows(data); }
    catch (e) { setError(getErrorMessage(e, "Kan werkbonnen niet laden.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [asset.id]);

  const maken = () => navigate(`/werkbonnen?new=1&loc=${asset.location_id || ""}&asset=${asset.id}`);

  return (
    <Card className="p-5">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-body font-semibold text-foreground">Werkbonnen</h2>
        {canWrite() && rows.length > 0 && (
          <Button size="sm" data-testid="asset-werkbon-maken-button" onClick={maken}>
            <Plus size={18} className="mr-1.5" /> Werkbon maken
          </Button>
        )}
      </div>
      {error ? (
        <InlineError testid="asset-werkbonnen-error" message={error} onRetry={load} />
      ) : loading ? (
        <p className="py-6 text-center text-body text-muted-foreground">Laden...</p>
      ) : rows.length === 0 ? (
        <InlineEmpty
          testid="asset-werkbonnen-empty"
          title="Nog geen werkbonnen op deze machine"
          purpose="Al het werk aan deze machine — met uren, materiaal en geld — komt hier te staan."
          action={canWrite() ? { label: "Werkbon maken", icon: Plus, onClick: maken } : null}
        />
      ) : (
        <div className="divide-y divide-border" data-testid="asset-werkbonnen-list">
          {rows.map((b) => (
            <button
              key={b.id}
              type="button"
              data-testid={`asset-werkbon-${b.id}`}
              onClick={() => navigate(`/werkbonnen/${b.id}`)}
              className="block w-full py-3 text-left transition-colors hover:bg-accent"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Code>{b.werkbon_number}</Code>
                  <WerkbonStatusBadge status={b.status} />
                </div>
                <Code size="small">{formatNlDate((b.closed_at || b.created_at || "").slice(0, 10))}</Code>
              </div>
              <div className="mt-1 text-body text-foreground">{b.omschrijving}</div>
              <div className="mt-0.5 flex flex-wrap gap-x-4 text-data text-muted-foreground">
                {b.uren_totaal > 0 && <span>{formatUren(b.uren_totaal)} uur</span>}
                {b.bedrag_totaal != null && b.bedrag_totaal !== 0 && <span>{formatEuro(b.bedrag_totaal)}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
};
